import { httpCapabilities } from "./capabilities";
import { Aux0ApiError, UnavailableDataError } from "./errors";
import type { Aux0Client, ConnectedAccount, Page, Project } from "./types";
import * as wire from "./wire";

export { Aux0ApiError, UnavailableDataError } from "./errors";

export interface HttpClientOptions {
  baseUrl: string;
  /** A real project id or an unambiguous slug from /me; never a fixture id. */
  project?: string;
  fetch?: typeof fetch;
  timeoutMs?: number;
  /** Called once per client on 401; the app clears its cache before navigating. */
  onUnauthorized?: () => void;
}
const unavailable = (method: string) => async (): Promise<never> => {
  throw new UnavailableDataError(method);
};

/** Console reads only. No public API keys, writes, or fixture fallbacks. */
export function createHttpClient(options: HttpClientOptions): Aux0Client {
  const baseUrl = options.baseUrl.replace(/\/$/, "");
  const fetcher = options.fetch ?? globalThis.fetch;
  let redirected = false;
  const inflight = new Map<string, Promise<unknown>>();

  function request<T>(path: string, parse: (value: unknown) => T): Promise<T> {
    const pending = inflight.get(path);
    if (pending) return pending as Promise<T>;
    const run = async () => {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), options.timeoutMs ?? 15_000);
      try {
        const response = await fetcher(baseUrl + path, {
          credentials: "include",
          signal: controller.signal,
          headers: { Accept: "application/json" },
          cache: "no-store",
        });
        if (response.status === 401 && !redirected) {
          redirected = true;
          options.onUnauthorized?.();
        }
        if (!response.ok) {
          // Do not echo arbitrary provider errors (or HTML) into the UI.
          const message =
            response.status === 401
              ? "Your session expired. Sign in to continue."
              : response.status === 403
                ? "You do not have permission to view this resource."
                : response.status === 404
                  ? "This resource was not found or is not accessible to your account."
                  : "The API could not load this resource. Try again.";
          throw new Aux0ApiError(response.status, path, message);
        }
        try {
          return parse(await response.json());
        } catch (error) {
          if (controller.signal.aborted) throw error;
          throw new Aux0ApiError(
            response.status,
            path,
            "The API returned an unexpected response.",
            "invalid_response",
          );
        }
      } catch (error) {
        if (error instanceof Aux0ApiError) throw error;
        throw new Aux0ApiError(
          0,
          path,
          controller.signal.aborted
            ? "The API took too long to respond. Try again."
            : "Could not reach the API. Check your connection and try again.",
          controller.signal.aborted ? "timeout" : "network",
        );
      } finally {
        clearTimeout(timeout);
      }
    };
    const result = run().finally(() => inflight.delete(path));
    inflight.set(path, result);
    return result;
  }

  const me = () => request("/apps/v1/me", wire.me);
  const projects = async (): Promise<Project[]> =>
    (await me()).organizations.flatMap((org) =>
      org.projects.map((project) => ({
        ...project,
        orgId: org.id,
        environments: ["sandbox", "production"],
      })),
    );
  const resolveProject = async (key: string) => {
    const all = await projects();
    const byId = all.find((project) => project.id === key);
    if (byId) return byId;
    const matches = all.filter((project) => project.slug === key);
    if (matches.length > 1)
      throw new UnavailableDataError(
        "workspace.project",
        "This project slug belongs to more than one organisation. Open the project using its project id.",
      );
    return matches[0];
  };
  const selectedProject = async () => {
    if (!options.project)
      throw new UnavailableDataError(
        "workspace.project",
        "Choose a project to view its data.",
      );
    const project = await resolveProject(options.project);
    if (!project)
      throw new Aux0ApiError(
        404,
        "/apps/v1/me",
        "This project was not found or is not accessible to your account.",
      );
    return project;
  };
  const projectPath = async (resource: string) =>
    "/apps/v1/projects/" +
    encodeURIComponent((await selectedProject()).id) +
    "/" +
    resource;
  const accounts = async () =>
    request(await projectPath("accounts"), (body) => wire.items(body, wire.account));
  function complete<T>(rows: T[], method: string): T[] {
    if (rows.length >= 200)
      throw new UnavailableDataError(
        method,
        "The API returns at most 200 rows. A complete result needs server pagination before this view can be shown.",
      );
    return rows;
  }
  function page<T>(
    rows: T[],
    query: { cursor?: string; limit?: number } = {},
    capped = false,
  ): Page<T> {
    const offset = query.cursor ? Number(query.cursor) : 0;
    if (
      !Number.isSafeInteger(offset) ||
      offset < 0 ||
      (query.cursor && String(offset) !== query.cursor)
    ) {
      throw new Aux0ApiError(400, "", "Invalid list cursor.");
    }
    const limit = query.limit ?? 200;
    if (!Number.isSafeInteger(limit) || limit < 1 || limit > 200)
      throw new Aux0ApiError(400, "", "Choose a limit between 1 and 200.");
    return {
      items: rows.slice(offset, offset + limit),
      total: capped ? undefined : rows.length,
      cursor: offset + limit < rows.length ? String(offset + limit) : undefined,
      truncated: capped,
    };
  }
  return {
    kind: "http",
    capabilities: httpCapabilities,
    workspace: {
      currentUser: async () => (await me()).user,
      projects,
      project: resolveProject,
      organization: async () => {
        const project = await selectedProject();
        const org = (await me()).organizations.find((org) => org.id === project.orgId);
        if (!org) throw new Aux0ApiError(404, "/apps/v1/me", "Organisation not found.");
        return { id: org.id, name: org.name, slug: org.slug };
      },
      members: unavailable("workspace.members"),
      invitations: unavailable("workspace.invitations"),
    },
    accounts: {
      list: async (filter = {}) => {
        const rows = await accounts();
        const term = filter.search?.trim().toLowerCase();
        return page(
          rows.filter(
            (row) =>
              (!filter.provider || row.provider === filter.provider) &&
              (!filter.status || row.status === filter.status) &&
              (!term ||
                row.handle.toLowerCase().includes(term) ||
                row.id.toLowerCase().includes(term)),
          ),
          filter,
          rows.length >= 200,
        );
      },
      get: async (id) => {
        const rows = await accounts();
        const found = rows.find((row) => row.id === id);
        if (!found) complete(rows, "accounts.get");
        return found;
      },
      needingAttention: async () =>
        complete(await accounts(), "accounts.needingAttention").filter(
          (row) => row.status === "needs_attention" || row.status === "disconnected",
        ),
      statusCounts: async () => {
        const counts: Record<ConnectedAccount["status"], number> = {
          connected: 0,
          syncing: 0,
          needs_attention: 0,
          paused: 0,
          disconnected: 0,
        };
        for (const row of complete(await accounts(), "accounts.statusCounts"))
          counts[row.status]++;
        return counts;
      },
      providerCounts: async () => {
        const counts = new Map<string, number>();
        for (const row of complete(await accounts(), "accounts.providerCounts"))
          counts.set(row.provider, (counts.get(row.provider) ?? 0) + 1);
        return [...counts];
      },
    },
    endUsers: {
      list: unavailable("endUsers.list"),
      get: unavailable("endUsers.get"),
    },
    hostedAuth: {
      create: unavailable("hostedAuth.create"),
    },
    mail: {
      threads: unavailable("mail.threads"),
      thread: unavailable("mail.thread"),
      emails: unavailable("mail.emails"),
      drafts: unavailable("mail.drafts"),
      folders: unavailable("mail.folders"),
    },
    calendar: {
      calendars: unavailable("calendar.calendars"),
      week: unavailable("calendar.week"),
      events: unavailable("calendar.events"),
      event: unavailable("calendar.event"),
      busy: unavailable("calendar.busy"),
    },
    contacts: {
      list: unavailable("contacts.list"),
      get: unavailable("contacts.get"),
      groups: unavailable("contacts.groups"),
    },
    platform: {
      metrics: unavailable("platform.metrics"),
      usage: unavailable("platform.usage"),
      apiKeys: async (environment) => {
        const keys = await request(await projectPath("keys"), (body) =>
          wire.items(body, wire.apiKey),
        );
        return keys.filter((key) => !environment || key.environment === environment);
      },
      webhooks: unavailable("platform.webhooks"),
      webhook: unavailable("platform.webhook"),
      deliveries: unavailable("platform.deliveries"),
      auditEvents: async (filter = {}) => {
        if (filter.environment)
          throw new UnavailableDataError(
            "platform.auditEvents",
            "The audit API does not report an environment, so it cannot filter by one.",
          );
        const events = await request(await projectPath("audit"), (body) =>
          wire.items(body, wire.auditEvent),
        );
        const term = filter.search?.trim().toLowerCase();
        return page(
          events.filter(
            (event) =>
              (!filter.actor || event.actor === filter.actor) &&
              (!filter.action || event.action === filter.action) &&
              (!term ||
                [event.summary, event.target, event.requestId].some((value) =>
                  value.toLowerCase().includes(term),
                )),
          ),
          filter,
          events.length >= 200,
        );
      },
    },
    billing: {
      period: unavailable("billing.period"),
      invoices: unavailable("billing.invoices"),
    },
    messaging: {
      chats: unavailable("messaging.chats"),
      chat: unavailable("messaging.chat"),
      messages: unavailable("messaging.messages"),
      chatsWithMessages: unavailable("messaging.chatsWithMessages"),
      profiles: unavailable("messaging.profiles"),
      invites: unavailable("messaging.invites"),
      posts: unavailable("messaging.posts"),
      comments: unavailable("messaging.comments"),
    },
  };
}
